import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Api } from '../api/axiosInstance';
import { validatePdfFileBytes } from '../../utils/fileValidation';

interface CandidateAnalysisState {
    analyzingCandidate: boolean;
    applicationData: any;
    error: string | null;
    success: boolean;

}

export const analyzeCandidateViaJob = createAsyncThunk<
    any,
    { jobId: number; candidateId: number },
    { rejectValue: string }
>(
    'job/analyzeCandidateViaJob',
    async ({ jobId, candidateId }, { rejectWithValue }) => {
        try {
            const res = await Api.post('/resume-parser/analyze-vs-job', {
                job_id: jobId,
                candidate_id: candidateId,
            });
            return res.data?.data ?? res.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || 'Failed to analyze candidate'
            );
        }
    }
);
export const resumeCandidateAIAnalyzerViaFile = createAsyncThunk<
  any,
  { file: File; job_id: number },
  { rejectValue: string }
>(
  'job/resumeCandidateAIAnalyzerViaFile',
  async ({ file, job_id }, { rejectWithValue }) => {
    try {
      // Safe validation using magic bytes
      const validation = await validatePdfFileBytes(file);
      if (validation !== true) {
        return rejectWithValue(validation);
      }

      const formData = new FormData();
      formData.append('resume', file);
      formData.append('job_id', job_id.toString());

      const response = await Api.post('/resume-parser/analyze-via-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data?.data ?? response.data;
    }  catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (typeof err.response?.data === 'string' && err.response.data) ||
        'Upload failed';
      return rejectWithValue(errorMessage);
    }
  }
);


const initialState: CandidateAnalysisState = {
    analyzingCandidate: false,
    applicationData: null,
    error: null,
    success: false,
};
const candidateAnalysisSlice = createSlice({
    name: 'candidateAnalysis',
    initialState,
    reducers: {
        clearAnalysisData(state) {
            state.applicationData = null;
            state.error = null;
            state.analyzingCandidate = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // Resume Upload
            .addCase(resumeCandidateAIAnalyzerViaFile.pending, (state) => {
                state.analyzingCandidate = true;
                state.error = null;
                state.success = false;
            })
            .addCase(resumeCandidateAIAnalyzerViaFile.fulfilled, (state, action) => {
                state.analyzingCandidate = false;
                state.applicationData = action.payload;
                state.success = true;
                localStorage.setItem('intQuestion', JSON.stringify(action.payload));
            })
            .addCase(resumeCandidateAIAnalyzerViaFile.rejected, (state, action) => {
                state.analyzingCandidate = false;
                state.error = action.payload || 'Analyze via file failed';
                state.success = false;
            })

            // Candidate Analysis
            .addCase(analyzeCandidateViaJob.pending, (state) => {
                state.analyzingCandidate = true;
                state.error = null;
                state.success = false;
            })
            .addCase(analyzeCandidateViaJob.fulfilled, (state, action) => {
                state.analyzingCandidate = false;
                state.applicationData = action.payload;
                state.success = true;
            })
            .addCase(analyzeCandidateViaJob.rejected, (state, action) => {
                state.analyzingCandidate = false;
                state.error = action.payload || 'Failed to analyze candidate';
                state.success = false;

            });
    },
});

export const { clearAnalysisData } = candidateAnalysisSlice.actions;

export default candidateAnalysisSlice.reducer;